import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Search, RefreshCw, ChevronRight } from "lucide-react";
import { useAdminLogbookDietsQuery } from "../api/adminLogbookApi";
import { LbStatusBadge, LbCountPill, fmtDate } from "../components/adminLogbookUi";

// Diet hub: pick a diet, then drill into its logbooks — the same shape as the
// admin upgrade-applications category hub.
export default function AdminLogbooksPage() {
  const { data, isLoading, isFetching, isError, refetch } = useAdminLogbookDietsQuery();
  const [q, setQ] = useState("");

  const diets: any[] = useMemo(
    () => (Array.isArray(data) ? data : Array.isArray((data as any)?.data) ? (data as any).data : []),
    [data]
  );

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return diets;
    return diets.filter((d) => String(d.title ?? "").toLowerCase().includes(nq));
  }, [diets, q]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <BookOpen className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Logbook Management</h1>
              <p className="text-sm text-gray-500 mt-0.5">Select a diet to review its logbooks and statuses.</p>
            </div>
          </div>
          <button onClick={() => refetch()} disabled={isFetching} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm">
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {/* Search */}
        <div className="mt-5 relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search diets…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>

        {/* Diet cards */}
        {isLoading ? (
          <div className="mt-6 text-sm text-gray-500">Loading diets…</div>
        ) : isError ? (
          <div className="mt-6 text-sm text-red-600">Failed to load logbook diets.</div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">No diets found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
            {filtered.map((d) => (
              <Link
                key={d.id}
                to={`/admin/logbooks/diet/${d.id}`}
                state={{ dietTitle: d.title }}
                className="group border rounded-xl p-4 hover:border-indigo-300 hover:shadow-sm transition bg-white"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      {d.title ?? "Untitled diet"}
                      {Number(d.is_active) === 1 && (
                        <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold uppercase">Active</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {fmtDate(d.start_date)} – {fmtDate(d.end_date)}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 shrink-0" />
                </div>

                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{d.logbooks_count ?? 0}</div>
                    <div className="text-xs text-gray-500">total logbooks</div>
                  </div>
                  <LbStatusBadge status={d.status} />
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                  {d.in_progress_count ? <span className="inline-flex items-center">In progress <LbCountPill tone="in_progress" count={d.in_progress_count} /></span> : null}
                  {d.submitted_count ? <span className="inline-flex items-center">Submitted <LbCountPill tone="submitted" count={d.submitted_count} /></span> : null}
                  {d.awaiting_count ? <span className="inline-flex items-center">Awaiting <LbCountPill tone="awaiting_assessment" count={d.awaiting_count} /></span> : null}
                  {d.graded_count ? <span className="inline-flex items-center">Graded <LbCountPill tone="graded" count={d.graded_count} /></span> : null}
                  {d.repeating_count ? <span className="inline-flex items-center">Repeating <LbCountPill tone="repeating" count={d.repeating_count} /></span> : null}
                  {d.completed_count ? <span className="inline-flex items-center">Completed <LbCountPill tone="completed" count={d.completed_count} /></span> : null}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
